/**
 * Canonical URL builder.
 *
 * The locked routing rule for Hantavirus is:
 *   - EN content lives at `/<path>`
 *   - non-English content lives at `/<locale>/<path>`
 *
 * Canonical URLs MUST therefore embed the locale prefix for non-English
 * pages and MUST omit it for English ones. Every page that returns
 * `documentProps.canonical` should call `canonicalUrl()` so the choice
 * of base host (multi-site) and the locale-prefix invariant are made
 * in exactly one place.
 */

import { getDescriptor, isLocaleCode } from "#/lib/i18n/locales.js";
import { siteBaseUrl, type SupportedLocale } from "./site.js";

/**
 * Build the canonical URL for a page.
 *
 * @param pathname - app-relative path, starts with `/`, no locale prefix
 *                   (e.g. `/about`, `/outbreak/argentina-2026`).
 * @param locale   - supported locale. Default "en".
 */
export function canonicalUrl(
  pathname: string,
  locale: SupportedLocale | string = "en",
): string {
  const base = siteBaseUrl();
  const cleanPath = normalisePath(pathname);
  if (locale === "en" || !locale) {
    return `${base}${cleanPath}`;
  }
  const localeSegment = isLocaleCode(locale) ? getDescriptor(locale).urlSegment : locale;
  // Strip any accidentally-supplied locale prefix to keep the contract
  // "canonicalUrl always takes a locale-less path" honest, and avoid
  // emitting `/ar/ar/...`.
  const stripped = cleanPath.replace(
    /^\/(en|ar|fr|zh-cn|es|de|ru|hi|id|ms)(?=\/|$)/i,
    "",
  );
  const final = stripped === "" ? "/" : stripped;
  return `${base}/${localeSegment}${final === "/" ? "" : final}`;
}

/**
 * The "raw" English URL (no locale prefix) for the supplied path.
 * Useful as the `href` for the `x-default` hreflang entry and as the
 * cluster identifier when generating sitemaps.
 */
export function canonicalDefault(pathname: string): string {
  return `${siteBaseUrl()}${normalisePath(pathname)}`;
}

function normalisePath(pathname: string): string {
  if (!pathname) return "/";
  // Force leading slash, collapse duplicate slashes, drop trailing slash
  // unless the whole path IS "/".
  const withLeading = pathname.startsWith("/") ? pathname : `/${pathname}`;
  const collapsed = withLeading.replace(/\/{2,}/g, "/");
  if (collapsed === "/") return "/";
  return collapsed.endsWith("/") ? collapsed.slice(0, -1) : collapsed;
}
